import { useState } from "react";
import { createPortal } from "react-dom";
import { KeyRound, X, Loader2 } from "lucide-react";
import { changePassword } from "@/lib/apiClient";
import { toast } from "sonner";

function detailMsg(d) {
  if (!d) return "Er ging iets mis.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

// variant: "full" (sidebar row) | "icon" (mobile header)
export default function ChangePassword({ variant = "full" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = () => { setOpen(false); setForm({ current_password: "", new_password: "", confirm: "" }); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) { setError("De nieuwe wachtwoorden komen niet overeen."); return; }
    if (form.new_password.length < 6) { setError("Nieuw wachtwoord moet minstens 6 tekens zijn."); return; }
    setLoading(true);
    try {
      await changePassword({ current_password: form.current_password, new_password: form.new_password });
      toast.success("Wachtwoord gewijzigd");
      close();
    } catch (err) {
      setError(detailMsg(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {variant === "full" ? (
        <button data-testid="change-password-btn" onClick={() => setOpen(true)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#5B6675] hover:bg-[#EDF1F5] transition-colors font-medium">
          <KeyRound className="h-5 w-5" strokeWidth={1.75} /> Wachtwoord wijzigen
        </button>
      ) : (
        <button data-testid="change-password-btn-mobile" onClick={() => setOpen(true)} title="Wachtwoord wijzigen"
          className="p-2 rounded-lg text-[#5B6675] hover:bg-[#EDF1F5] transition-colors">
          <KeyRound className="h-5 w-5" strokeWidth={1.75} />
        </button>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-md fe-fade-up" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E7EC]">
              <h3 className="font-heading font-semibold text-xl text-[#16202B]">Wachtwoord wijzigen</h3>
              <button onClick={close} className="p-2 rounded-lg hover:bg-[#EDF1F5]"><X className="h-5 w-5 text-[#5B6675]" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <label className="block">
                <span className="text-sm text-[#5B6675] mb-1.5 block">Huidig wachtwoord</span>
                <input data-testid="cp-current" type="password" value={form.current_password} onChange={set("current_password")} className="fe-input" required />
              </label>
              <label className="block">
                <span className="text-sm text-[#5B6675] mb-1.5 block">Nieuw wachtwoord</span>
                <input data-testid="cp-new" type="password" value={form.new_password} onChange={set("new_password")} className="fe-input" placeholder="Minstens 6 tekens" required />
              </label>
              <label className="block">
                <span className="text-sm text-[#5B6675] mb-1.5 block">Herhaal nieuw wachtwoord</span>
                <input data-testid="cp-confirm" type="password" value={form.confirm} onChange={set("confirm")} className="fe-input" required />
              </label>
              {error && <p data-testid="cp-error" className="text-sm text-[#B0413E] bg-[#F3DEDD] rounded-xl px-4 py-3">{error}</p>}
              <div className="flex justify-end gap-3 pt-1">
                <button type="button" onClick={close} className="px-5 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5]">Annuleren</button>
                <button data-testid="cp-submit" type="submit" disabled={loading} className="px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A] disabled:opacity-50 flex items-center gap-2">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} Opslaan
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
